import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { ChangeEvent, JSX, useEffect, useState } from "react";
import { useCreateCertificateMutation } from "../../../../common/api/certificate";
import { useGetQualificationsQuery } from "../../../../common/api/qualification";
import { handleCreateCertificate } from "../../utils/handleCreateCertificate";
import { SelectFieldComponent } from "../../../../common/components/SelectFieldComponent";
import { getQualificationOptions } from "../../../../common/utils/selectOptions/getQualificationOptions";
import {
    setFormValues,
    setIsPostCreateCertificateSuccess,
} from "../../CreateCertificateModalSlice";
import { SubmitButtonComponent } from "../../../../common/components/SubmitButtonComponent";
import { FileInputFieldComponent } from "../FormComponents/FileInputFieldComponent";
import { DateInputFieldComponent } from "../FormComponents/DateInputFieldComponent";
import { useEstablishmentId } from "../../../../common/utils/useEstablishmentId";
import { QualificationTypeEnum } from "../../../../common/enums/QualificationTypeEnum";
import Skeleton from "react-loading-skeleton";
import { QualificationInterface } from "../../../../common/interfaces/QualificationInterface";

export const EstablishmentCertificateForm = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const [file, setFile] = useState<File | null>(null);
    const establishmentId = useEstablishmentId();
    const { formValues } = useAppSelector(
        (state) => state.createCertificateModal,
    );

    const [
        postCreateCertificate,
        {
            isLoading: isPostCreateCertificateLoading,
            isSuccess: isPostCreateCertificateSuccess,
        },
    ] = useCreateCertificateMutation();

    const { data: qualificationData, isLoading: qualificationDataIsLoading } =
        useGetQualificationsQuery({
            items_per_page: 9999,
            filter: {
                type: QualificationTypeEnum.ESTABLISHMENT,
            },
        });

    useEffect(() => {
        if (!isPostCreateCertificateSuccess) {
            dispatch(setIsPostCreateCertificateSuccess(false));
        }
    }, [isPostCreateCertificateSuccess, dispatch]);

    if (qualificationDataIsLoading) {
        return <Skeleton count={5} borderRadius={"0.8rem"} height={"5.5rem"} />;
    }

    return (
        <form
            className={"c-popup__content__body"}
            onSubmit={(event) => {
                event.preventDefault();
                if (!isPostCreateCertificateLoading) {
                    handleCreateCertificate({
                        formValues,
                        file,
                        postCreateCertificate,
                        dispatch,
                        isEstablishmentCertificate: true,
                        establishmentId,
                    });
                }
            }}
        >
            <SelectFieldComponent
                id={1}
                options={getQualificationOptions(qualificationData?.data)}
                onChange={(newValue) => {
                    const selectedQualification = qualificationData?.data?.find(
                        (item: QualificationInterface) =>
                            item.id.toString() === newValue.value,
                    );

                    dispatch(
                        setFormValues({
                            ...formValues,
                            qualification: {
                                ...formValues?.qualification,
                                selectedId: newValue.value,
                                selectedValue: newValue.label,
                                validityPeriod:
                                    selectedQualification?.validity_period ??
                                    null,
                                validityType:
                                    selectedQualification?.validity_type ??
                                    null,
                                fixedValidUntil:
                                    selectedQualification?.fixed_valid_until ??
                                    null,
                            },
                        }),
                    );
                }}
                value={{
                    value: formValues?.qualification?.selectedId || "",
                    label: formValues?.qualification?.selectedValue || "",
                }}
                errorMessage={"Kwalificatie is verplicht"}
                placeHolder={"Zoeken..."}
                title={"Selecteer kwalificatie"}
                showValidityPeriod={true}
                validityPeriod={formValues?.qualification?.validityPeriod}
                validityType={formValues?.qualification?.validityType}
                fixedValidUntil={formValues?.qualification?.fixedValidUntil}
            />

            <DateInputFieldComponent
                id={4}
                value={formValues?.validAt}
                expiresAt={formValues?.expiresAt}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    dispatch(
                        setFormValues({
                            ...formValues,
                            validAt: event.target.value,
                        }),
                    )
                }
            />

            <FileInputFieldComponent
                id={5}
                formValues={formValues}
                dispatch={dispatch}
                setFile={setFile}
            />

            <SubmitButtonComponent
                label={"Certificaat toevoegen"}
                isLoading={isPostCreateCertificateLoading}
            />
        </form>
    );
};
